import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import SEO from '../components/SEO';
import { SERVICES_CONFIG } from '../data/servicesConfig';
import { CITY_DATA } from '../data/cityData';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CheckIcon = () => (
  <svg className="w-5 h-5 text-[#3a87fe] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
  </svg>
);

const SERVICE_FEATURES = {
  'kitchen-remodeling': ['Custom Kitchen Cabinets & Cabinetry CT', 'Granite & Quartz Countertop Installation', 'Tile Backsplash Design & Installation', 'Kitchen Lighting & Electrical Upgrades', 'Hardwood & Tile Flooring Installation', 'Kitchen Appliance Installation & Hookup'],
  'bathroom-remodeling': ['Custom Tile & Natural Stone Work CT', 'Bathroom Vanity & Cabinet Installation', 'Walk-in Shower Construction & Glass Enclosures', 'Modern Plumbing Fixture Upgrades', 'ADA Accessible Bathroom Modifications', 'Radiant Heated Bathroom Flooring'],
  'deck-building': ['Trex Composite Decking Installation CT', 'Custom Multi-Level Deck Design', 'Aluminum & Composite Railing Systems', 'Built-In Bench Seating & Planters', 'Pergola & Shade Structure Construction', 'Low-Voltage Deck Lighting Installation'],
  'basement-finishing': ['Basement Framing & Insulation CT', 'Basement Flooring — LVP, Tile, Carpet', 'Recessed Lighting & Electrical Wiring', 'Basement Waterproofing & Moisture Control', 'Custom Built-In Shelving & Storage', 'Egress Window Installation Connecticut'],
  'vinyl-siding': ['Vinyl Siding Installation & Replacement CT', 'Trim, Soffit & Fascia Installation', 'Insulated Siding for Energy Efficiency', 'Siding Color Consultation & Selection', 'Manufacturer Warranty Service & Support', 'Fascia Board Repair & Replacement'],
  'window-replacement': ['Energy-Efficient Double & Triple Pane Windows', 'Custom Window Sizes & Configurations', 'Professional Window Installation CT', 'Interior & Exterior Window Trim Work', 'Bay & Bow Window Installation', 'Casement, Slider & Picture Windows'],
  'custom-furniture': ['Custom Furniture Design & Fabrication CT', 'Quality Hardwood & Live-Edge Materials', 'Built-In Cabinetry, Shelving & Bookshelves', 'Custom Aquarium Design & Installation', 'Dining Tables, Desks & Entertainment Centers', 'Farmhouse Tables & Rustic Furniture', 'Furniture Refinishing & Restoration', 'Built-In Fish Tank Walls & Aquarium Cabinetry'],
  'home-maintenance': ['General Home Repairs CT', 'Interior & Exterior House Painting', 'Drywall Patching & Repair', 'Door Hanging & Trim Installation', 'Seasonal Home Maintenance Packages', 'Power Washing, Gutters & Weatherproofing'],
};

const CITY_LIST = Object.keys(CITY_DATA);

const ServicePage = () => {
  const { serviceSlug } = useParams();
  const service = SERVICES_CONFIG[serviceSlug];
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    axios.get(`${API_URL}/api/testimonials`)
      .then(res => {
        const all = res.data.testimonials || [];
        const matching = all.filter(t => t.service && service && t.service.toLowerCase().includes(service.name?.toLowerCase()?.split(' ')[0]));
        setTestimonials(matching.length >= 2 ? matching.slice(0, 2) : all.slice(0, 2));
      })
      .catch(() => {});
  }, [service]);

  if (!service) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h1 className="text-4xl font-bold mb-4">Service Not Found</h1>
        <Link to="/services" className="text-[#3a87fe] hover:underline">View All Services</Link>
      </div>
    );
  }

  const features = SERVICE_FEATURES[serviceSlug] || [];
  const faq = service.faq || [];

  const faqSchema = faq.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faq.map(item => ({
      "@type": "Question",
      "name": item.q,
      "acceptedAnswer": { "@type": "Answer", "text": item.a }
    }))
  } : null;

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": `${service.name} in Connecticut`,
    "provider": {
      "@type": "GeneralContractor",
      "name": "Visser General Construction LLC",
      "url": "https://cthomeremodels.com",
      "telephone": "+1-203-604-3202",
      "address": { "@type": "PostalAddress", "streetAddress": "42 Gay St", "addressLocality": "Meriden", "addressRegion": "CT", "postalCode": "06450", "addressCountry": "US" }
    },
    "areaServed": { "@type": "State", "name": "Connecticut" },
    "description": service.description,
    "url": `https://cthomeremodels.com/services/${service.slug}`
  };

  const combinedSchema = [serviceSchema];
  if (faqSchema) combinedSchema.push(faqSchema);

  const seoData = {
    title: `${service.name} Connecticut | ${service.shortDesc} CT | Visser Construction Meriden`,
    description: `Professional ${service.name.toLowerCase()} services in Connecticut. ${service.description} Serving Meriden, Wallingford, Old Saybrook, Madison, Guilford & CT shoreline. Licensed & insured. Free estimates.`,
    keywords: `${service.name} Connecticut, ${service.name.toLowerCase()} CT, ${service.name.toLowerCase()} Meriden CT, Connecticut ${service.name.toLowerCase()} contractor, ${service.name.toLowerCase()} near me CT, best ${service.name.toLowerCase()} contractor Connecticut, ${service.name.toLowerCase()} cost CT, affordable ${service.name.toLowerCase()} Wallingford CT, ${service.name.toLowerCase()} Old Saybrook CT`,
    canonical: `https://cthomeremodels.com/services/${service.slug}`,
    ogImage: `https://cthomeremodels.com/og/v2/services.png`,
    schemaData: combinedSchema
  };

  return (
    <div className="service-page">
      <SEO {...seoData} />

      {/* Hero */}
      <section
        className="relative py-32 md:py-40 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.5)), url('${service.heroImage}')`
        }}
        data-testid="service-hero"
      >
        <div className="absolute top-6 left-6 z-10">
          <img
            src="/api/media/name/visser-general-construction-llc-logo-connecticut.png"
            alt={`Visser Construction - ${service.name} Contractor Connecticut`}
            className="h-20 md:h-28 object-contain drop-shadow-2xl"
          />
        </div>
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white drop-shadow-lg leading-tight">
            {service.name} in Connecticut
          </h1>
          <p className="text-base md:text-lg max-w-4xl mx-auto text-white drop-shadow-md">
            {service.description} Serving Meriden, Wallingford, Old Saybrook, Madison, Guilford, and Connecticut shoreline communities. Licensed & insured.
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16">
            <div>
              <img
                src={service.image1}
                alt={`${service.name} project completed in Connecticut by Visser Construction licensed insured contractor`}
                className="w-full h-80 object-cover rounded-xl shadow-lg"
                loading="lazy"
              />
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-4 text-[#5c0701]">Professional {service.name} Services in CT</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                {service.description} Visser General Construction delivers expert craftsmanship for homeowners throughout Connecticut.
                From initial consultation to final walkthrough, we handle every detail with care and precision.
              </p>
              <div className="space-y-3">
                {features.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckIcon />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Project Photos with Captions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            <figure className="rounded-lg overflow-hidden shadow-md">
              <img src={service.image2} alt={`Professional ${service.name.toLowerCase()} project completed in Meriden CT by licensed insured contractor`} className="w-full h-64 object-cover" loading="lazy" />
              <figcaption className="bg-gray-50 px-4 py-3 text-sm text-gray-600">{service.caption2 || `${service.name} project completed in Meriden, Connecticut`}</figcaption>
            </figure>
            <figure className="rounded-lg overflow-hidden shadow-md">
              <img src={service.image3} alt={`${service.name} renovation in Wallingford CT - quality craftsmanship by Visser Construction`} className="w-full h-64 object-cover" loading="lazy" />
              <figcaption className="bg-gray-50 px-4 py-3 text-sm text-gray-600">{service.caption3 || `${service.name} renovation for Connecticut homeowner`}</figcaption>
            </figure>
            <figure className="rounded-lg overflow-hidden shadow-md">
              <img src={service.image4} alt={`${service.name} installation in Old Saybrook CT - licensed insured Connecticut contractor`} className="w-full h-64 object-cover" loading="lazy" />
              <figcaption className="bg-gray-50 px-4 py-3 text-sm text-gray-600">{service.caption4 || `${service.name} work by Visser General Construction LLC`}</figcaption>
            </figure>
          </div>

          {/* Why Choose Us Section */}
          {service.whyChooseUs && (
            <div className="mb-16">
              <h2 className="text-3xl font-bold mb-6 text-[#5c0701]">Why Connecticut Homeowners Choose Visser Construction</h2>
              <p className="text-gray-700 leading-relaxed text-lg mb-6">
                {service.whyChooseUs}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
                <div className="bg-gray-50 rounded-lg p-5 text-center">
                  <div className="text-[#3a87fe] font-bold text-2xl mb-2">Licensed</div>
                  <p className="text-gray-600 text-sm">CT Licensed General Contractor — fully permitted work on every project</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-5 text-center">
                  <div className="text-[#3a87fe] font-bold text-2xl mb-2">Insured</div>
                  <p className="text-gray-600 text-sm">Full liability insurance and workers' compensation for your protection</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-5 text-center">
                  <div className="text-[#3a87fe] font-bold text-2xl mb-2">Local</div>
                  <p className="text-gray-600 text-sm">Meriden-based, serving CT homeowners within 40 miles — we know your community</p>
                </div>
              </div>
            </div>
          )}

          {/* Our Process Section */}
          {service.processSteps && service.processSteps.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-8 mb-16">
              <h2 className="text-3xl font-bold mb-8 text-center text-[#5c0701]">Our {service.name} Process</h2>
              <div className="space-y-6">
                {service.processSteps.map((step, idx) => (
                  <div key={idx} className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#5c0701] text-white flex items-center justify-center font-bold text-lg">
                      {idx + 1}
                    </div>
                    <div className="pt-2">
                      <p className="text-gray-700 text-lg">{step}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Testimonials Section */}
          {testimonials.length > 0 && (
            <div className="bg-[#f4efe9] rounded-xl p-8 mb-16">
              <h2 className="text-3xl font-bold mb-8 text-center text-[#5c0701]">What Our Customers Say</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {testimonials.map((t, idx) => (
                  <div key={idx} className="bg-white rounded-lg p-6 shadow-sm">
                    <div className="flex mb-3">
                      {[1,2,3,4,5].map(i => <svg key={i} className={`w-5 h-5 ${i <= t.rating ? 'text-yellow-400' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>)}
                    </div>
                    <p className="text-gray-700 italic mb-4">"{t.text}"</p>
                    <p className="font-semibold text-sm text-[#5c0701]">- {t.name}{t.city ? `, ${t.city}` : ''}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* FAQ Section */}
          {faq.length > 0 && (
            <div className="mb-16" data-testid="service-faq">
              <h2 className="text-3xl font-bold mb-8 text-[#5c0701]">Frequently Asked Questions — {service.name} in Connecticut</h2>
              <div className="space-y-6">
                {faq.map((item, idx) => (
                  <div key={idx} className="border-b border-gray-200 pb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">{item.q}</h3>
                    <p className="text-gray-700 leading-relaxed">{item.a}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Cities We Serve */}
          <div className="bg-gray-50 rounded-xl p-8 mb-16">
            <h2 className="text-3xl font-bold mb-6 text-center">{service.name} Services Throughout Connecticut</h2>
            <p className="text-gray-700 text-center mb-8">
              We serve homeowners in these Connecticut communities:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {CITY_LIST.map((cityId) => {
                const city = CITY_DATA[cityId];
                return (
                  <Link
                    key={cityId}
                    to={`/services/${service.slug}/${cityId}`}
                    className="flex items-center gap-2 py-2 px-3 rounded-lg hover:bg-white hover:shadow-sm transition text-gray-700 hover:text-[#3a87fe]"
                    data-testid={`service-city-${cityId}`}
                  >
                    <svg className="w-4 h-4 text-[#3a87fe] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm font-medium">{city.name}, CT</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#5c0701] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6">{service.ctaHeading || `Ready to Start Your ${service.name} Project?`}</h2>
          <p className="text-base md:text-lg mb-8 max-w-2xl mx-auto">
            {service.ctaSubtext || `Contact Visser Construction today for a free estimate on ${service.name.toLowerCase()} in Connecticut`}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-[#3a87fe] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#2968d9] transition"
              data-testid="service-cta-contact"
            >
              Get Free Estimate
            </Link>
            <a
              href="tel:2036043202"
              className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-[#5c0701] transition"
              data-testid="service-cta-call"
            >
              Call (203) 604-3202
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicePage;
